import { useEffect, useRef, useState, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ThemeProvider";

// Eager-loaded pages (landing, auth - critical path)
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";

// Lazy-loaded pages (dashboard & secondary routes)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Releases = lazy(() => import("@/pages/Releases"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Earnings = lazy(() => import("@/pages/Earnings"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const Settings = lazy(() => import("@/pages/Settings"));
const Upload = lazy(() => import("@/pages/Upload"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const AudioServices = lazy(() => import("@/pages/AudioServices"));
const Publishing = lazy(() => import("@/pages/Publishing"));
const MerchStore = lazy(() => import("@/pages/MerchStore"));
const SyncLicensing = lazy(() => import("@/pages/SyncLicensing"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Contact = lazy(() => import("@/pages/Contact"));
const MixingDashboard = lazy(() => import("@/pages/MixingDashboard"));
const PublishingDashboard = lazy(() => import("@/pages/PublishingDashboard"));
const PreSavePage = lazy(() => import("@/pages/PreSavePage"));
const PartnerPortal = lazy(() => import("@/pages/PartnerPortal"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const PodcastDashboard = lazy(() => import("@/pages/PodcastDashboard"));
const PlaylistPitching = lazy(() => import("@/pages/PlaylistPitching"));
const PodcastHostingPage = lazy(() => import("@/pages/PodcastHostingPage"));
const DistributionPage = lazy(() => import("@/pages/DistributionPage"));
const LabelDashboard = lazy(() => import("@/pages/LabelDashboard"));
const AudienceDashboard = lazy(() => import("@/pages/AudienceDashboard"));
const GoogleAuthCallback = lazy(() => import("@/pages/GoogleAuthCallback"));
const ReleaseDetails = lazy(() => import("@/pages/ReleaseDetails"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DistributionAgreement = lazy(() => import("@/pages/DistributionAgreement"));
const EasySongLicensing = lazy(() => import("@/pages/EasySongLicensing"));
const SmartLinkPage = lazy(() => import("@/pages/SmartLinkPage"));
const HelpGuide = lazy(() => import("@/pages/HelpGuide"));

// Curator Portal
const CuratorRegister = lazy(() => import("@/pages/CuratorRegister"));
const CuratorLogin = lazy(() => import("@/pages/CuratorLogin"));
const CuratorDashboard = lazy(() => import("@/pages/CuratorDashboard"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin w-10 h-10 border-3 border-primary border-t-transparent rounded-full"></div>
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

// Redirect www to non-www immediately
if (window.location.hostname === 'www.syce.me') {
  window.location.href = window.location.href.replace('www.syce.me', 'syce.me');
}

// Automatically determine backend URL based on current hostname
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  console.log("[SYCE API Config] Hostname:", hostname);
  console.log("[SYCE API Config] Origin:", origin);
  
  // Production domains - always use syce.me (not www)
  if (hostname === 'syce.me' || hostname === 'www.syce.me') {
    const productionUrl = 'https://syce.me';
    console.log("[SYCE API Config] Production mode - using:", productionUrl);
    return productionUrl;
  }
  
  // Preview/development - use environment variable
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const backendUrl = envUrl || origin;
  console.log("[SYCE API Config] Dev/Preview mode - using:", backendUrl);
  return backendUrl;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;
console.log("[SYCE API Config] Final API URL:", API);

// Helper function to get auth headers for API calls
export function getAuthHeaders() {
  const token = localStorage.getItem("syce_token");
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  };
}

// Helper for authenticated fetch calls
export async function authFetch(url, options = {}) {
  const token = localStorage.getItem("syce_token");
  const headers = {
    "Authorization": `Bearer ${token}`,
    ...options.headers
  };
  return fetch(url, { ...options, headers });
}

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// Simple Auth Provider
function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log("[AuthProvider] Checking auth...");
      const token = localStorage.getItem("syce_token");
      console.log("[AuthProvider] Token exists:", !!token);
      
      if (!token) {
        console.log("[AuthProvider] No token, setting not authenticated");
        setIsLoading(false);
        return;
      }

      try {
        console.log("[AuthProvider] Calling /auth/me...");
        const response = await fetch(`${API}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` },
          credentials: "include"
        });

        console.log("[AuthProvider] /auth/me status:", response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log("[AuthProvider] User authenticated:", userData.email);
          setUserState(userData);
        } else {
          console.log("[AuthProvider] Auth failed, clearing token");
          localStorage.removeItem("syce_token");
        }
      } catch (error) {
        console.error("[AuthProvider] Auth check error:", error);
        localStorage.removeItem("syce_token");
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const setUser = useCallback((userData) => {
    setUserState(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("syce_token");
    setUserState(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Simple Protected Route
function ProtectedRoute({ children }) {
  const { user, isLoading } = useAuth();

  console.log("[ProtectedRoute] isLoading:", isLoading, "user:", !!user);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    console.log("[ProtectedRoute] No user, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  return children;
}

// OAuth Callback Handler
function OAuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleOAuth = async () => {
      const hash = window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      
      // Clear hash
      window.history.replaceState(null, '', window.location.pathname);
      
      // Only call session endpoint if we have a session_id
      if (match && match[1]) {
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: match[1] }),
            credentials: "include"
          });

          if (response.ok) {
            const data = await response.json();
            if (data.token) {
              localStorage.setItem("syce_token", data.token);
            }
            setUser(data.user);
            navigate("/dashboard", { replace: true });
            return;
          }
        } catch (error) {
          console.error("OAuth error:", error);
        }
      }
      // No session_id or auth failed - go to login
      navigate("/login", { replace: true });
    };

    handleOAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
    </div>
  );
}

// App Router
function AppRouter() {
  const location = useLocation();

  // ALWAYS clear any session_id hash - it causes problems
  useEffect(() => {
    if (window.location.hash?.includes("session_id")) {
      console.log("[AppRouter] Clearing stale session_id hash");
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [location]);

  // Only process OAuth if coming directly from Google auth (not from our own login)
  // Check if hash exists AND we don't already have a token (meaning fresh OAuth)
  const hasSessionId = window.location.hash?.includes("session_id=");
  const hasToken = localStorage.getItem("syce_token");
  
  if (hasSessionId && !hasToken && location.pathname !== "/login") {
    return <OAuthCallback />;
  }
  
  // Clear hash if we have a token already (stale OAuth attempt)
  if (hasSessionId && hasToken) {
    window.history.replaceState(null, '', window.location.pathname);
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/audio-services" element={<AudioServices />} />
        <Route path="/publishing" element={<Publishing />} />
        <Route path="/merch" element={<MerchStore />} />
        <Route path="/sync" element={<SyncLicensing />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/podcast-hosting" element={<PodcastHostingPage />} />
        <Route path="/distribution" element={<DistributionPage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/presave/:campaignId" element={<PreSavePage />} />
        <Route path="/link/:linkId" element={<SmartLinkPage />} />
        <Route path="/partners/*" element={<PartnerPortal />} />
        {/* SYCE Google OAuth routes */}
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/auth/google/success" element={<GoogleAuthCallback />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/dashboard/mixing" element={<ProtectedRoute><MixingDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/publishing" element={<ProtectedRoute><PublishingDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/podcast" element={<ProtectedRoute><PodcastDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/pitching" element={<ProtectedRoute><PlaylistPitching /></ProtectedRoute>} />
        <Route path="/dashboard/label" element={<ProtectedRoute><LabelDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/audience" element={<ProtectedRoute><AudienceDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/licensing" element={<ProtectedRoute><EasySongLicensing /></ProtectedRoute>} />
        <Route path="/releases" element={<ProtectedRoute><Releases /></ProtectedRoute>} />
        <Route path="/releases/:releaseId" element={<ProtectedRoute><ReleaseDetails /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
        <Route path="/marketing" element={<ProtectedRoute><Marketing /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/dashboard/help" element={<ProtectedRoute><HelpGuide /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/distribution-agreement" element={<DistributionAgreement />} />
        
        {/* Curator Portal Routes */}
        <Route path="/curator/register" element={<CuratorRegister />} />
        <Route path="/curator/login" element={<CuratorLogin />} />
        <Route path="/curator/dashboard" element={<CuratorDashboard />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <div className="noise-overlay"></div>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
        <Toaster position="bottom-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
